import { SeoHead } from "@/components/SeoHead";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import {
  Library,
  BookMarked,
  PenLine,
  Send,
  Sparkles,
  Download,
  Lock,
  KeyRound,
  Rss,
  StickyNote,
} from "lucide-react";
import { gallery } from "@/data/gallery";
import { MarketingNav } from "@/components/marketing/MarketingNav";
import { MarketingFooter } from "@/components/marketing/MarketingFooter";
import { AppShot } from "@/components/marketing/AppShot";
import libraryShot from "@/assets/marketing/library.webp";
import excerptsShot from "@/assets/marketing/excerpts.webp";
import composerShot from "@/assets/marketing/composer.webp";
import imageStudioShot from "@/assets/marketing/imagestudio.webp";



const STEPS = [
  {
    icon: Library,
    title: "Organize",
    blurb:
      "Build your library of books, articles, chapters, sites, reports, and podcasts — with citations resolved through ISBNdb for books and DOI for academic sources.",
  },
  {
    icon: BookMarked,
    title: "Capture",
    blurb:
      "Attach excerpts to their source with author, page, and notes intact — or pull highlights straight in from Readwise.",
  },
  {
    icon: PenLine,
    title: "Write",
    blurb:
      "Draft Pieces in the Composer — turn AI off and write quietly, or invoke planning and writing agents grounded in your own Corpus.",
  },
  {
    icon: Send,
    title: "Publish",
    blurb:
      "Send to Kit, Bento, WordPress, Substack, and more from one unified flow — or export a finished excerpt as a branded image in Image Studio.",
  },
];

/** The three features that get a full product-frame row on the homepage. */
const SHOWCASE = [
  {
    eyebrow: "My Library",
    title: "A searchable, citable library",
    body:
      "Books, articles, chapters, sites, and reports kept as proper sources — with ISBNdb-powered book metadata, DOI-resolved academic citations, and optional Amazon enrichment.",
    href: "/features/my-library/",
    bullets: [
      "Typed sources: book, article, chapter, website, report",
      "Citations resolved, not retyped",
      "Excerpts always point back to one source of truth",
    ],
    mock: (
      <AppShot
        src={libraryShot}
        alt="My Library in Excerptus, showing typed sources and search"
        label="My Library"
        caption="Sources, typed and cited"
      />
    ),

    flip: false,
  },
  {
    eyebrow: "Composer",
    title: "Your voice, with or without AI",
    body:
      "Write quietly with AI off, or plan a Piece, approve the Brief, and draft on the Canvas with planning and writing agents grounded in your own Corpus.",
    href: "/features/composer/",
    bullets: [
      "Document chips show exactly what grounded a line",
      "Footnotes, endnotes, and bibliography on every draft",
      "AI entirely off is a first-class mode",
    ],
    mock: (
      <AppShot
        src={composerShot}
        alt="The Excerptus Composer with the Draft Canvas and AI chat rail"
        label="Composer"
        caption="Draft Canvas, rails, and citations"
      />
    ),

    flip: true,
  },
  {
    eyebrow: "Image Studio & Publishing",
    title: "One flow, many destinations",
    body:
      "Image Studio for branded excerpt images, plus a single publish step that ships to Kit, Bento, WordPress, and unofficial Substack drafts — and files every published Piece into the Archive.",
    href: "/features/image-studio/",
    bullets: [
      "Branded excerpt images in one click",
      "One publish step, many connectors",
      "The Archive remembers everything you've shipped",
    ],
    mock: (
      <AppShot
        src={imageStudioShot}
        alt="Image Studio in Excerptus generating a branded excerpt image"
        label="Image Studio"
        caption="Excerpt to image in one click"
      />
    ),

    flip: false,
  },
];

/** The remaining features, shown as a card grid. */
const FEATURES = [
  {
    icon: Rss,
    title: "Read and listen in one place",
    body:
      "Subscribe to RSS feeds and podcasts inside Excerptus, with synced transcripts on episodes — and promote anything worth keeping into your library.",
    href: "/features/podcasts-feed-reader/",
  },
  {
    icon: BookMarked,
    title: "Excerpts and canonical citations",
    body:
      "Every saved excerpt links back to its library source, so author and citation are always one source of truth. Plus, footnote, endnote, and bibliography functions for all content drafts.",
    href: "/features/my-excerpts/",
  },
  {
    icon: StickyNote,
    title: "Notes for everything in between",
    body:
      "Plain-text Notes in a folder tree that sits next to your library and your drafts — quick capture without leaving the app.",
    href: "/features/notes/",
  },
];

const TRUST = [
  {
    icon: Lock,
    title: "Encrypted at rest, in transit, and per workspace",
    body: "Your notes, drafts, excerpts, source summaries, and author voice profiles are encrypted in our database with a key derived per workspace — even a leaked backup stays unreadable. TLS protects everything in transit, and you can rotate your workspace's encryption key yourself from Settings whenever you want.",
  },
  {
    icon: Sparkles,
    title: "Your words are never used to train AI",
    body: "Nothing you write, capture, or generate in Excerptus is fed back into a training set — ours or anyone else's. AI requests are sent only to fulfill the action you took, and you can switch AI off entirely and still use every capture, library, and writing surface.",
  },
  {
    icon: Download,
    title: "Yours to take with you, yours to erase",
    body: "Export any workspace as structured JSON from Settings → Danger zone, including library items, excerpts, notes, drafts, and citations. The same panel can permanently delete a non-primary workspace — typed confirmation, no soft-delete, no shadow copies left behind.",
  },
  {
    icon: KeyRound,
    title: "Your connected accounts stay locked down",
    body: "Readwise tokens, bring-your-own AI keys, and other third-party credentials are encrypted with a separate token key — never the same key that protects your content. Disconnect any integration and the stored secret is destroyed in the same action.",
  },
];

const Landing = () => {


  return (
    <div className="min-h-screen bg-background text-foreground">
      <SeoHead
        title="Excerptus — The Heart of Your Second Brain for Writing & Research"
        description="Excerptus is the heart of a second brain for writers and researchers — a citable library and canonical excerpts that ground every piece you publish."
      />
      <MarketingNav />

      {/* Hero — two column: copy left, product frame right */}
      <section className="mx-auto max-w-6xl px-4 pt-14 pb-16 sm:px-6 sm:pt-20 sm:pb-24">
        <div className="grid items-center gap-10 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)] lg:gap-14">
          <div>
            <p className="mkt-eyebrow">For writers, researchers &amp; knowledge workers</p>
            <h1 className="mt-4 font-serif text-4xl font-semibold leading-[1.1] sm:text-5xl">
              Research. Write. Publish.
              <br />
              <span className="text-primary">One Heart.</span> One Source of Truth.
            </h1>
            <h2 className="mt-5 font-serif text-xl font-medium sm:text-2xl">
              The Heart of a Second Brain for Writers and Researchers
            </h2>
            <p className="mt-5 max-w-xl text-base text-muted-foreground sm:text-lg">
              Excerptus is the Heart of the Knowledge Worker's second brain — a citable library and canonical excerpts that flow into your corpus to ground your research into the content you publish.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <Link to="/auth/">
                <Button size="lg" className="min-w-40">Start free trial</Button>
              </Link>
              <Link to="/pricing/">
                <Button variant="outline" size="lg">See pricing</Button>
              </Link>
            </div>
            <p className="mt-5 text-sm text-muted-foreground">10-day free trial on every plan.</p>
          </div>
          <div className="lg:pl-4">
            <AppShot
              src={excerptsShot}
              alt="My Excerpts in Excerptus, showing captured quotes with their sources"
              label="My Excerpts"
              caption="Quotes, captured and cited"
            />
          </div>

        </div>
      </section>

      {/* How it works */}
      <section className="mkt-band-alt">
        <div className="mkt-band-inner">
          <p className="mkt-eyebrow">How it works</p>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold leading-tight sm:text-4xl">
            Organize, capture, write, publish — without losing the trail.
          </h2>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {STEPS.map(({ icon: Icon, title, blurb }, i) => (
              <div key={title} className="rounded-lg border border-border bg-card p-5">
                <div className="mb-4 flex items-center gap-2.5">
                  <span className="grid h-7 w-7 place-items-center rounded-md bg-primary/15 text-primary">
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <span className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
                    Step {i + 1}
                  </span>
                </div>
                <h3 className="font-serif text-lg font-semibold">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{blurb}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Product tour — alternating copy / product frame */}
      <section className="mkt-band">
        <div className="mkt-band-inner space-y-20 sm:space-y-28">
          {SHOWCASE.map((s) => (
            <div
              key={s.title}
              className={`grid items-center gap-10 lg:gap-14 ${
                s.flip
                  ? "lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]"
                  : "lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]"
              }`}
            >
              <div className={`min-w-0 ${s.flip ? "lg:order-2" : ""}`}>

                <p className="mkt-eyebrow">{s.eyebrow}</p>
                <h2 className="mt-3 font-serif text-2xl font-semibold leading-tight sm:text-3xl">
                  {s.title}
                </h2>
                <p className="mt-4 text-base leading-relaxed text-muted-foreground">{s.body}</p>
                <ul className="mt-5 space-y-2">
                  {s.bullets.map((b) => (
                    <li key={b} className="flex gap-2.5 text-sm text-muted-foreground">
                      <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" aria-hidden />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={s.href}
                  className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
                >
                  Learn more →
                </Link>
              </div>
              <div className={`min-w-0 ${s.flip ? "lg:order-1" : ""}`}>{s.mock}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Desktop & Mobile stance */}
      <section className="mkt-band">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="font-serif text-3xl font-semibold sm:text-4xl">Desktop for Deep Work. Mobile for Capture.</h2>
          <p className="mt-5 text-lg text-muted-foreground">
            Research, long-form writing, and publishing want a real screen. Capture, review, and reading on the go need your phone. Excerptus embraces both — an uncompromising desktop workspace, and a calm mobile companion built for the things you actually do on the go. One workspace, two approaches.
          </p>
          <Link
            to="/features/desktop-and-mobile/"
            className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
          >
            See how the split works →
          </Link>
        </div>
      </section>

      {/* Feature deep-dive */}
      <section className="mkt-band-alt">
        <div className="mkt-band-inner">
          <p className="mkt-eyebrow">Built end to end</p>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold leading-tight sm:text-4xl">
            Every surface a Knowledge Worker needs — AI on or AI off.
          </h2>
          <p className="mt-3 max-w-2xl text-muted-foreground">
            Wired into one source of truth.
          </p>

          <div className="mt-10 grid gap-5 md:grid-cols-3">
            {FEATURES.map(({ icon: Icon, title, body, href }) => (
              <Link
                key={title}
                to={href}
                className="group rounded-lg border border-border bg-card p-6 transition-colors hover:border-primary/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 text-primary">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <h3 className="font-serif text-lg font-semibold group-hover:text-primary">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>




      {/* Gallery */}
      <section id="gallery" className="mkt-band-alt">
        <div className="mkt-band-inner">
          <p className="mkt-eyebrow">Image Studio</p>
          <h2 className="mt-3 font-serif text-3xl font-semibold leading-tight sm:text-4xl">
            From Excerpt to Image in One Click
          </h2>
          <p className="mt-3 text-muted-foreground">A few quotes generated in Image Studio.</p>
          <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-4 md:grid-cols-3 lg:grid-cols-4">
            {gallery.map((item) => (
              <figure
                key={item.src}
                className="overflow-hidden rounded-lg border border-border bg-card transition-colors hover:border-primary/50"
              >
                <picture>
                  {item.avif && <source srcSet={item.avif} type="image/avif" />}
                  <img
                    src={item.src}
                    alt={`${item.work} — ${item.author}`}
                    loading="lazy"
                    decoding="async"
                    className="aspect-square w-full object-cover"
                  />
                </picture>
                <figcaption className="p-3">
                  <div className="truncate font-serif text-sm font-medium text-foreground">
                    {item.work}
                  </div>
                  <div className="truncate text-xs text-muted-foreground">{item.author}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* Built for Knowledge Workers */}
      <section className="mkt-band">
        <div className="mx-auto max-w-4xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="font-serif text-3xl font-semibold sm:text-4xl">Built for Knowledge Workers</h2>
          <p className="mt-5 text-lg text-muted-foreground">
            If your job is to read, think, and write — and if what you publish has to be defensible — Excerptus keeps the trail intact. Sources stay typed and cited. Excerpts stay attached to the work they came from. Drafts stay grounded in your personal corpus, not some AI model's generic guess.
          </p>
        </div>
      </section>

      {/* Trust + control */}
      <section className="mkt-band">
        <div className="mkt-band-inner">
          <p className="mkt-eyebrow">Built to protect what you write</p>
          <h2 className="mt-3 max-w-2xl font-serif text-3xl font-semibold leading-tight sm:text-4xl">
            Encryption, ownership, and a clean exit — by default, not as an upsell.
          </h2>
          <div className="mt-10 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            {TRUST.map(({ icon: Icon, title, body }) => (
              <div key={title} className="rounded-lg border border-border bg-card p-6">
                <span className="mb-4 inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/15 text-primary">
                  <Icon className="h-4 w-4" aria-hidden />
                </span>
                <h3 className="font-serif text-base font-semibold leading-snug">{title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="mkt-band">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex flex-col items-start justify-between gap-6 rounded-lg border border-border bg-card p-8 sm:flex-row sm:items-center">
            <div>
              <h2 className="font-serif text-2xl font-semibold sm:text-3xl">Give Your Second Brain a Heart</h2>
              <p className="mt-2 text-muted-foreground">10-day free trial on every plan.</p>
            </div>
            <Link to="/auth/" className="shrink-0">
              <Button size="lg" className="min-w-40">Start free trial</Button>
            </Link>
          </div>
        </div>
      </section>

      <MarketingFooter />
    </div>
  );
};

export default Landing;
